import {
  gameInfo,
  skockoLabels,
  skockoSymbols,
  type GameId,
  type GameScores,
  type GameState,
  type SkockoSymbol,
} from '@elena/shared';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Composer, type MicSignal } from '../components/Composer';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { session } from '../lib/session';
import { speak } from '../lib/speech';
import { useElena } from '../state';

export const symbolGlyph: Record<SkockoSymbol, string> = {
  skocko: '☺',
  tref: '♣',
  pik: '♠',
  herc: '♥',
  karo: '♦',
  zvezda: '★',
};

const order: GameId[] = ['kaladont', 'asocijacije', 'moj_broj', 'skocko', 'ko_zna_zna'];

export function Games({ micSignal }: { micSignal: MicSignal }) {
  const { game, messages, status } = useElena();
  const [scores, setScores] = useState<GameScores>({});
  const [say, setSay] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const deviceId = session.deviceId();
  const voice = Boolean(status?.voice.enabled);

  const loadScores = useCallback(async () => {
    try {
      setScores((await api.games(deviceId)).scores);
    } catch {
      // the board still works
    }
  }, [deviceId]);

  useEffect(() => {
    void loadScores();
  }, [loadScores, game?.status]);

  // Voice moves come back as Elena's chat replies; show the latest one on the board.
  const lastElena = [...messages].reverse().find((m) => m.role === 'elena');
  useEffect(() => {
    if (lastElena && game) setSay(lastElena.text);
  }, [lastElena?.id]);

  const reply = (r: { say: string }) => {
    setSay(r.say);
    if (session.speakReplies()) void speak(r.say, voice);
  };

  const run = async (fn: () => Promise<{ say: string }>) => {
    setBusy(true);
    try {
      reply(await fn());
    } catch (err) {
      setSay(err instanceof ApiError ? err.message : 'Nešto nije u redu.');
    } finally {
      setBusy(false);
    }
  };

  const move = (text: string) => run(() => api.moveGame(deviceId, text));

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>Igre</h1>
          <p className="muted">
            Igraj sa Elenom glasom, kucanjem ili klikom. Reci i „hajde da igramo kaladont“ bilo gde
            u aplikaciji.
          </p>
        </div>
        {game && (
          <div className="row gap-8">
            <button type="button" className="btn" onClick={() => setShowRules((v) => !v)}>
              Pravila
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => void run(() => api.startGame(deviceId, game.game))}
            >
              Nova partija
            </button>
            <button
              type="button"
              className="btn danger"
              onClick={() => void run(() => api.stopGame(deviceId))}
            >
              Kraj
            </button>
          </div>
        )}
      </header>

      {!game ? (
        <div className="game-grid">
          {order.map((id) => (
            <button
              key={id}
              type="button"
              className="card game-card"
              disabled={busy}
              onClick={() => void run(() => api.startGame(deviceId, id))}
            >
              <span className="game-card-title">{gameInfo[id].name}</span>
              <span className="muted">{gameInfo[id].hint}</span>
              <span className="muted small">
                {scores[id]
                  ? `Odigrano ${scores[id].played} · rekord ${scores[id].best}`
                  : 'Još nije igrano'}
              </span>
              <span className="game-card-play">
                <Icon name="play" size={16} /> Igraj
              </span>
            </button>
          ))}
        </div>
      ) : (
        <div className="game-layout">
          <section className="card game-board" aria-label={gameInfo[game.game].name}>
            <header className="card-head">
              <h2>{gameInfo[game.game].name}</h2>
              <StatusPill state={game} />
            </header>
            {showRules && <p className="notice subtle">{gameInfo[game.game].rules}</p>}
            {game.game === 'kaladont' && <Kaladont state={game} onMove={move} busy={busy} />}
            {game.game === 'moj_broj' && <MojBroj state={game} onMove={move} busy={busy} />}
            {game.game === 'skocko' && <Skocko state={game} onMove={move} busy={busy} />}
            {game.game === 'asocijacije' && <Asocijacije state={game} onMove={move} busy={busy} />}
            {game.game === 'ko_zna_zna' && <KoZnaZna state={game} onMove={move} busy={busy} />}
          </section>

          <aside className="card game-side" aria-live="polite">
            <span className="muted small">Elena</span>
            <p className="game-say">{say ?? 'Spremna sam.'}</p>
            <p className="muted small">Možeš i glasom: pritisni mikrofon i reci potez.</p>
            <Composer listenSignal={micSignal} />
          </aside>
        </div>
      )}
    </div>
  );
}

function StatusPill({ state }: { state: GameState }) {
  const label =
    state.status === 'playing'
      ? 'u toku'
      : state.status === 'won'
        ? 'pobeda'
        : state.status === 'lost'
          ? 'poraz'
          : 'kraj';
  const tone = state.status === 'won' ? 'ok' : state.status === 'lost' ? 'bad' : 'idle';
  return <span className={`pill ${tone}`}>{label}</span>;
}

type BoardProps<G extends GameState['game']> = {
  state: Extract<GameState, { game: G }>;
  onMove: (move: string) => void;
  busy: boolean;
};

function MoveInput({
  placeholder,
  onMove,
  busy,
  disabled,
}: {
  placeholder: string;
  onMove: (m: string) => void;
  busy: boolean;
  disabled?: boolean;
}) {
  const [value, setValue] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    onMove(value.trim());
    setValue('');
  };
  return (
    <form className="row gap-8" onSubmit={submit}>
      <label className="sr-only" htmlFor="game-move">
        Potez
      </label>
      <input
        id="game-move"
        className="field grow-field"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={disabled}
        autoComplete="off"
        autoFocus
      />
      <button type="submit" className="btn primary" disabled={busy || disabled || !value.trim()}>
        Pošalji
      </button>
    </form>
  );
}

function Kaladont({ state, onMove, busy }: BoardProps<'kaladont'>) {
  return (
    <div className="stack-12">
      <div className="kaladont-need">
        <span className="muted small">Sledeća reč počinje sa</span>
        <span className="kaladont-letters">{state.need.toUpperCase()}</span>
        {state.strikes > 0 && (
          <span className="muted small">Neispravnih reči: {state.strikes} od 3</span>
        )}
      </div>
      <ol className="word-chain">
        {state.words.map((w, i) => (
          <li key={i} className={`word word-${w.by}`}>
            {w.word}
          </li>
        ))}
      </ol>
      <MoveInput
        placeholder={`Reč na „${state.need}“ (ne na „ka“)`}
        onMove={onMove}
        busy={busy}
        disabled={state.status !== 'playing'}
      />
    </div>
  );
}

function MojBroj({ state, onMove, busy }: BoardProps<'moj_broj'>) {
  const [expr, setExpr] = useState<Array<{ text: string; index?: number }>>([]);
  const used = new Set(expr.flatMap((t) => (t.index === undefined ? [] : [t.index])));
  const add = (text: string, index?: number) => setExpr((e) => [...e, { text, index }]);
  const done = state.status !== 'playing';
  return (
    <div className="stack-12">
      <div className="mb-target">
        <span className="muted small">Traženi broj</span>
        <span className="mb-number">{state.target}</span>
      </div>
      <div className="mb-numbers">
        {state.numbers.map((n, i) => (
          <button
            key={i}
            type="button"
            className="mb-chip"
            disabled={done || used.has(i)}
            onClick={() => add(String(n), i)}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="mb-ops">
        {['+', '−', '×', '÷', '(', ')'].map((op) => (
          <button key={op} type="button" className="mb-op" disabled={done} onClick={() => add(op)}>
            {op}
          </button>
        ))}
        <button
          type="button"
          className="mb-op"
          aria-label="Obriši poslednje"
          disabled={done || expr.length === 0}
          onClick={() => setExpr((e) => e.slice(0, -1))}
        >
          ⌫
        </button>
      </div>
      <div className="mb-expr" aria-live="polite">
        {expr.map((t) => t.text).join(' ') || (
          <span className="muted">Klikni brojeve i znakove…</span>
        )}
      </div>
      <div className="row gap-8 wrap">
        <button
          type="button"
          className="btn primary"
          disabled={busy || done || expr.length === 0}
          onClick={() =>
            onMove(
              expr
                .map((t) => t.text)
                .join('')
                .replace(/×/g, '*')
                .replace(/÷/g, '/')
                .replace(/−/g, '-'),
            )
          }
        >
          Proveri
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy || done}
          onClick={() => onMove('predajem')}
        >
          Pokaži rešenje
        </button>
      </div>
      {state.attempt && (
        <p>
          Ti: <span className="mono">{state.attempt.expression}</span> ={' '}
          <strong>{state.attempt.value}</strong>
        </p>
      )}
      {state.elena && (
        <p>
          Elena: <span className="mono">{state.elena.expression}</span> ={' '}
          <strong>{state.elena.value}</strong>
        </p>
      )}
    </div>
  );
}

function Skocko({ state, onMove, busy }: BoardProps<'skocko'>) {
  const [guess, setGuess] = useState<SkockoSymbol[]>([]);
  const done = state.status !== 'playing';
  useEffect(() => setGuess([]), [state.attempts.length]);
  const rows = Array.from({ length: state.maxAttempts }, (_, i) => state.attempts[i]);
  return (
    <div className="stack-12">
      <ol className="skocko-rows">
        {rows.map((row, i) => {
          const current = !row && i === state.attempts.length && !done;
          const symbols = row ? row.guess : current ? guess : [];
          return (
            <li key={i} className={`skocko-row${current ? ' is-current' : ''}`}>
              <span className="skocko-slots">
                {[0, 1, 2, 3].map((k) => (
                  <span
                    key={k}
                    className="skocko-slot"
                    aria-label={symbols[k] ? skockoLabels[symbols[k]] : 'prazno'}
                  >
                    {symbols[k] ? symbolGlyph[symbols[k]] : ''}
                  </span>
                ))}
              </span>
              <span
                className="skocko-pegs"
                aria-label={row ? `${row.hits} crvenih, ${row.near} žutih` : undefined}
              >
                {[0, 1, 2, 3].map((k) => (
                  <span
                    key={k}
                    className={`peg${row && k < row.hits ? ' hit' : row && k < row.hits + row.near ? ' near' : ''}`}
                  />
                ))}
              </span>
            </li>
          );
        })}
      </ol>
      {state.solution && (
        <p>
          Rešenje:{' '}
          {state.solution.map((s, i) => (
            <span key={i} className="skocko-slot inline">
              {symbolGlyph[s]}
            </span>
          ))}
        </p>
      )}
      <div className="skocko-palette">
        {skockoSymbols.map((s) => (
          <button
            key={s}
            type="button"
            className="skocko-pick"
            disabled={done || guess.length >= 4}
            onClick={() => setGuess((g) => [...g, s])}
            aria-label={skockoLabels[s]}
          >
            <span aria-hidden="true">{symbolGlyph[s]}</span>
          </button>
        ))}
        <button
          type="button"
          className="skocko-pick"
          aria-label="Obriši"
          disabled={done || guess.length === 0}
          onClick={() => setGuess((g) => g.slice(0, -1))}
        >
          ⌫
        </button>
      </div>
      <button
        type="button"
        className="btn primary"
        disabled={busy || done || guess.length !== 4}
        onClick={() => onMove(guess.join(' '))}
      >
        Proveri
      </button>
    </div>
  );
}

function Asocijacije({ state, onMove, busy }: BoardProps<'asocijacije'>) {
  const letters = ['A', 'B', 'C', 'D'];
  const done = state.status !== 'playing';
  return (
    <div className="stack-12">
      <div className="asoc-grid">
        {state.columns.map((col, c) => (
          <div key={c} className="asoc-col">
            {col.fields.map((f, i) => (
              <button
                key={i}
                type="button"
                className={`asoc-field${f ? ' is-open' : ''}`}
                disabled={busy || done || Boolean(f) || Boolean(col.solution)}
                onClick={() => onMove(`otvori ${letters[c]}${i + 1}`)}
              >
                {f ?? `${letters[c]}${i + 1}`}
              </button>
            ))}
            {col.solution ? (
              <div className="asoc-solution">{col.solution.toUpperCase()}</div>
            ) : (
              <ColumnGuess letter={letters[c]!} onMove={onMove} disabled={busy || done} />
            )}
          </div>
        ))}
      </div>
      <div className="asoc-final">
        {state.final ? (
          <div className="asoc-solution final">{state.final.toUpperCase()}</div>
        ) : (
          <ColumnGuess letter="konačno" onMove={onMove} disabled={busy || done} />
        )}
        <span className="muted small">Poeni: {state.score}</span>
        {!done && (
          <button type="button" className="btn" onClick={() => onMove('predajem')}>
            Predajem
          </button>
        )}
      </div>
    </div>
  );
}

function ColumnGuess({
  letter,
  onMove,
  disabled,
}: {
  letter: string;
  onMove: (m: string) => void;
  disabled: boolean;
}) {
  const [value, setValue] = useState('');
  return (
    <form
      className="asoc-guess"
      onSubmit={(e) => {
        e.preventDefault();
        if (!value.trim()) return;
        onMove(letter === 'konačno' ? `konačno ${value.trim()}` : `${letter}: ${value.trim()}`);
        setValue('');
      }}
    >
      <input
        className="field"
        placeholder={letter === 'konačno' ? 'Konačno rešenje' : `Rešenje ${letter}`}
        aria-label={letter === 'konačno' ? 'Konačno rešenje' : `Rešenje kolone ${letter}`}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={disabled}
      />
    </form>
  );
}

function KoZnaZna({ state, onMove, busy }: BoardProps<'ko_zna_zna'>) {
  const done = state.status !== 'playing';
  return (
    <div className="stack-12">
      <div className="row gap-8">
        <span className="pill idle">
          {done ? 'Kraj' : `Pitanje ${state.index + 1} / ${state.total}`}
        </span>
        <span className="push strong">Poeni: {state.score}</span>
      </div>
      {state.last && (
        <p className={state.last.correct ? 'kzz-ok' : 'kzz-bad'}>
          {state.last.correct ? 'Tačno!' : `Tačan odgovor: ${state.last.answer}`}
        </p>
      )}
      {state.question && <p className="kzz-question">{state.question}</p>}
      {!done && (
        <>
          <MoveInput placeholder="Tvoj odgovor" onMove={onMove} busy={busy} />
          <button type="button" className="btn" disabled={busy} onClick={() => onMove('dalje')}>
            Dalje (bez poena)
          </button>
        </>
      )}
    </div>
  );
}
